// src/components/SubmitTime/components/DynamicCustomFields.tsx
// Sub-component for rendering dynamic custom fields
// Dynamically imports and renders field components based on FieldTypeId

import React from 'react';
import type { CustomFieldConfig } from '../types';

// Import all custom field components
import {
  InputTextField,
  NumericIntegerField,
  NumericDecimalField,
  InputDateField,
  HyperLinkField,
  ComboBoxField,
  MultiSelectField,
  RichTextEditor,
} from '../fields';

interface DynamicCustomFieldsProps {
  customFields: CustomFieldConfig[] | null;
  canEdit: boolean;
  apiConfig?: {
    apiUrl?: string;
    token?: string;
  };
  disableAutoSave?: boolean;
  configValueId?: number;
  layerId?: number;
  projectId?: number | null;
  handleDynamicInputChange: (customFieldId: number, value: string | number) => void;
  updateCustomFields?: (customField: CustomFieldConfig) => void;
}

export const DynamicCustomFields: React.FC<DynamicCustomFieldsProps> = ({
  customFields,
  canEdit,
  apiConfig,
  disableAutoSave = false,
  configValueId,
  layerId,
  projectId,
  handleDynamicInputChange,
  updateCustomFields,
}) =>
{
  if (!customFields || customFields.length === 0)
  {
    return null;
  }

  // Filter out special fields (Estimated, Remaining, Actual, Phases)
  const specialFieldNames = ['Estimated', 'Remaining', 'Actual', 'Phases'];
  const displayFields = customFields.filter(field => !specialFieldNames.includes(field.Name));

  // Render a single custom field based on its type
  const renderField = (field: CustomFieldConfig) =>
  {
    const isRequiredEmpty = field.Required;
    const commonProps: any = {
      customField: field,
      canEdit: canEdit && field.Editable,
      apiConfig,
      disableAutoSave,
      configValueId,
      layerId,
      projectId,
      handleDynamicInputChange,
      UpdateCustomFields: updateCustomFields,
      showRequiredError: isRequiredEmpty,
    };

    switch (field.FieldTypeId)
    {
      case 1: // Text
        return <InputTextField key={field.CustomFieldId} {...commonProps} />;

      case 2: // NumericInteger
        return <NumericIntegerField key={field.CustomFieldId} {...commonProps} />;

      case 3: // Text
        return <InputTextField key={field.CustomFieldId} {...commonProps} />;

      case 4: // NumericDecimal
        return <NumericDecimalField key={field.CustomFieldId} {...commonProps} />;

      case 5: // Text
        return <InputTextField key={field.CustomFieldId} {...commonProps} />;

      case 6: // Date
        return <InputDateField key={field.CustomFieldId} {...commonProps} />;

      case 7: // MultiSelect
        return <MultiSelectField key={field.CustomFieldId} {...commonProps} />;

      case 8: // ComboBox (single select)
        return <ComboBoxField key={field.CustomFieldId} {...commonProps} />;

      case 9: // ComboBox (single select)
        return <ComboBoxField key={field.CustomFieldId} {...commonProps} />;

      case 10: // RichText
        return <RichTextEditor key={field.CustomFieldId} {...commonProps} />;

      case 11: // HyperLink
        return <HyperLinkField key={field.CustomFieldId} {...commonProps} />;

      default:
        console.warn(`Unknown field type: ${field.FieldTypeId} for field ${field.Name}`);
        return null;
    }
  };

  return (
    <div className="dynamic-custom-fields">
      {displayFields.map(field => (
        <div key={field.CustomFieldId} className="corgee-layout-tableSubmitTimet">
          <div
            className={`corgee-layout-table-cellSubmitTimet layout-width-20SubmitTime ${field.Required && (field.Value === null || field.Value === '' || field.Value === undefined) ? 'submit-required-label' : undefined}`}
          >
            <span className={field.Required ? 'submit-required-label' : undefined}>
              {field.Name}:
            </span>
          </div>
          <div className="corgee-layout-table-cellSubmitTimet">
            {renderField(field)}
            {field.Required &&
              (field.Value === null || field.Value === '' || field.Value === undefined) && (
              <span className="submit-required-field" style={{ fontSize: '11px' }}>
                {field.Name} is required
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DynamicCustomFields;
